import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Trash2 } from "lucide-react";
import { preventivoApi, type BollettaFuori, type BollettaVista, type Condominio, type Contatore, type Vista } from "@/api/preventivo";
import { ApiError } from "@/api/client";
import { AuthContext } from "@/auth/AuthContext";
import { pick } from "@/lib/scegliFile";
import { comprimiFoto } from "@/lib/comprimiFoto";
import { bolletteDelCondominio, nomiDellaVista } from "@/lib/preventivoFormato";
import { condominiDelLink, potaSpunte, scambia } from "@/lib/linkPreventivo";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Testata } from "@/components/preventivo/Testata";
import { CodaCaricamento } from "@/components/preventivo/CodaCaricamento";
import { AvvisoErrore, BolletteFuori, PulsanteRiprova, Voce, VoceFoto, Icona } from "@/components/preventivo/BolletteFuori";
import { StrisciaStato } from "@/components/preventivo/StrisciaStato";
import { FotoBolletta } from "@/components/preventivo/FotoBolletta";
import { troppeBollette, useCodaFoto } from "@/components/preventivo/useCodaFoto";
import { SchedaCondominio } from "@/components/preventivo/SchedaCondominio";
import { TabellaCondomini } from "@/components/preventivo/TabellaCondomini";
import { PannelloOfferta } from "@/components/preventivo/PannelloOfferta";
import { leggiCondivisi, scartaCondivisi } from "@/pwa/condivisi";
import { ModoPreventivo } from "@/components/preventivo/modo";
import { RiquadroAmministratore } from "@/components/preventivo/RiquadroAmministratore";
import { InvioAmministratore } from "@/components/preventivo/InvioAmministratore";
import { BarraTutti, PannelloTutti, RiquadroTutti, TastoInvia } from "@/components/preventivo/TuttiCondomini";
import {
  ALLEGATI_NON_RICEVUTI, APERTURA, APERTURA_PASSI, COLLEGAMENTO_NON_RIUSCITO, ELIMINA, FILE_NON_ACCETTATO, INVIO_NON_RIUSCITO, NESSUNA_OFFERTA, RIPROVA,
  TESTO_ELIMINA_BOLLETTA, raggiuntoMassimo, titoloEliminaBolletta,
} from "@/components/preventivo/scritte";
import {
  ELIMINA_PREVENTIVO, ESCI_SENZA_SALVARE, TESTO_USCITA, TORNA_LISTA, USCIRE_SENZA_SALVARE, testoElimina, titoloElimina,
} from "@/components/preventivo/scritteLista";
import { useAvvisoUscita } from "@/components/preventivo/useAvvisoUscita";
import { FOCUS } from "@/components/preventivo/parti";

const INTERVALLO_MS = 2_000;
const ACCETTA_FILE = "application/pdf,image/jpeg,image/png";
const ACCETTA_FOTO = "image/jpeg,image/png";

/** I file di una condivisione di Android, con il suo identificativo (serve a scartarla senza toccarne un'altra). */
type Condivisione = { id: string; files: File[] };

/** Tolgo `?condivisi=…` dall'indirizzo, senza ricaricare e senza aggiungere una voce alla cronologia. */
function togliCondivisi() {
  const u = new URL(window.location.href);
  u.searchParams.delete("condivisi");
  window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
}

/** Un file da mandare: una bolletta nuova, o una pagina in più di `bollettaId`. */
interface Lavoro {
  chiave: string;
  file: File;
  bollettaId?: string;
  /** Il preventivo dove il file è stato aggiunto: parte lì anche se la pagina intanto ne mostra un altro. */
  pid: string;
}

/** File che il server non ha preso: rifiutato (413/415) o non partito per rete o guasto (si può rimandare). */
interface Scarto {
  chiave: string;
  file: File;
  pid: string;
  rifiutato: boolean;
  /** Non è entrato perché il preventivo ha già il massimo di bollette: la riga dice questo, senza «Riprova». */
  tetto?: boolean;
}

/** Un comando fallito su una bolletta, da poter ripetere: le pagine non inviate, o la foto nuova che aspetta
 *  l'eliminazione della vecchia. */
interface Fallito {
  tipo: "pagina" | "elimina";
  files: File[];
}

/** Dove è stata fatta la scelta d'offerta che non è stata salvata: l'avviso va lì, e solo lì. «riga»: i menù di una
 *  fornitura (riga sottile della tabella, scheda del telefono), con `contatore`. */
interface SceltaFallita {
  chiama: () => Promise<Vista>;
  dove: "pannello" | "intestazione" | "riga" | "tutti" | "pannelloTutti";
  condominio: string;
  contatore?: string;
}

/** La bolletta di cui si sta chiedendo conferma per l'eliminazione. */
interface DaEliminare {
  id: string;
  mese: string | null;
}

/** Si continua a chiedere finché c'è una prima lettura in corso, un'anagrafica non ancora letta (il condominio
 *  arriva con lei) o una seconda lettura in corso. */
function daInterrogare(v: Vista): boolean {
  const bollette = v.condomini.flatMap(bolletteDelCondominio);      // anche le righe gas
  return v.lette < v.totale
    || v.bollette_fuori.some((b) => !b.anagrafica_letta)
    || bollette.some((b) => !b.anagrafica_letta || b.dettaglio === "in_corso");
}

/** Cosa è aperto per scegliere l'offerta: il pannello dal basso (schede) o, nella tabella, il riquadro sotto la riga di
 *  titolo del condominio («testata»); i menù delle forniture sono sempre aperti (03/11). Uno stato solo per la pagina:
 *  schede e tabella sono montate insieme, ma si apre una cosa alla volta. */
type Aperto =
  | { dove: "tutti"; condominio?: undefined }
  | { dove: "invio"; condominio?: undefined }
  | { dove: "pannello"; condominio: string }
  | { dove: "testata"; condominio: string };

/** Ciò che è aperto resta aperto solo se il suo condominio c'è ancora nella vista nuova: altrimenti si chiude, e non si
 *  riapre da sé quando quella voce torna. */
function restaAperto(a: Aperto | null, v: Vista): Aperto | null {
  // i pannelli di «Stessa offerta per tutti» e d'invio non dipendono da un condominio
  if (a?.dove === "tutti" || a?.dove === "invio") return a;
  if (a === null) return null;
  return v.condomini.some((x) => x.chiave === a.condominio) ? a : null;
}

function senza(f: Record<string, Fallito>, id: string): Record<string, Fallito> {
  return Object.fromEntries(Object.entries(f).filter(([k]) => k !== id));
}

/** La pagina del preventivo: quale, lo dice l'indirizzo (`/preventivo-condomini/:id`). Cambiando l'indirizzo sulla stessa
 *  rotta la pagina riparte dal preventivo nuovo. Senza id (la rotta di prima della lista) l'id è vuoto. */
export function PreventivoCondomini() {
  const { id: pid = "" } = useParams();
  const navigate = useNavigate();
  const pidRef = useRef(pid);
  pidRef.current = pid;
  const modo = useMemo(() => ({ solaLettura: false, fileUrl: (b: string) => preventivoApi.fileUrl(pid, b) }), [pid]);
  const [vista, setVista] = useState<Vista | null>(null);
  const [errore, setErrore] = useState(false);
  const [riprova, setRiprova] = useState(0);
  const [coda, setCoda] = useState<Lavoro[]>([]);
  const [scarti, setScarti] = useState<Scarto[]>([]);
  const [confermaElimina, setConfermaElimina] = useState(false);
  const [eliminaFallita, setEliminaFallita] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  // «Elimina» di una bolletta (03/11): la conferma, un errore da dire lì, la chiamata in corso
  const [daEliminare, setDaEliminare] = useState<DaEliminare | null>(null);
  const [eliminaBollettaFallita, setEliminaBollettaFallita] = useState(false);
  const [eliminandoBolletta, setEliminandoBolletta] = useState(false);
  const eliminandoBollettaRef = useRef(false);   // un secondo tocco prima del disegno non ne fa due
  // il riquadro Amministratore ha un salvataggio in corso o un valore non salvato: i tasti d'invio aspettano
  const [adminPendente, setAdminPendente] = useState(false);
  const uscita = useAvvisoUscita(adminPendente);
  const eliminandoRef = useRef(false);
  const [falliti, setFalliti] = useState<Record<string, Fallito>>({});
  const [sceltaFallita, setSceltaFallita] = useState<SceltaFallita | null>(null);
  const [aperto, setAperto] = useState<Aperto | null>(null);
  const [scegliendo, setScegliendo] = useState(false);
  // le spunte di «Quali condomìni mettere nel link?» vivono qui, così restano a ogni vista nuova; quelle dei condomìni
  // che spariscono dalla vista se ne vanno con loro
  // i condomìni tolti dal link: gli altri sono spuntati, così il pannello d'invio parte da tutti (Alfonso, 29/10)
  const [tolti, setTolti] = useState<Set<string>>(() => new Set());
  // il link per l'amministratore ha un interruttore suo per agenzia (giro 5): finché non si sa che è acceso, il
  // riquadro non c'è
  const [linkAttivo, setLinkAttivo] = useState(false);
  useEffect(() => {
    let viva = true;
    Promise.resolve()
      .then(() => preventivoApi.stato())
      .then((r) => { if (viva) setLinkAttivo(r?.link === true); })
      .catch(() => { /* resta spento */ });
    return () => { viva = false; };
  }, []);
  const mettiVista = useCallback((v: Vista) => {
    setVista(v);
    setTolti((s) => potaSpunte(s, v));
  }, []);
  const scegliendoRef = useRef(false);
  // Allegati condivisi da WhatsApp: `?condivisi=1` si legge una volta, all'apertura
  const condivisiRef = useRef(new URLSearchParams(window.location.search).get("condivisi"));
  const [letti, setLetti] = useState<Condivisione | null | undefined>(undefined);   // undefined = non ancora letti
  const [entrano, setEntrano] = useState<Condivisione | null>(null);     // letti: entrano al disegno dopo, con i posti liberi giusti
  const [allegatiNonRicevuti, setAllegatiNonRicevuti] = useState(false);

  const richiesta = useRef(0);
  const codaRef = useRef<Lavoro[]>([]);
  const inVolo = useRef(false);
  const contatore = useRef(0);
  const liberiRef = useRef(Infinity);

  // Vale solo l'ultima risposta chiesta: una lenta arrivata dopo non riporta indietro la pagina.
  const ricarica = useCallback(async () => {
    const n = ++richiesta.current;
    try {
      const v = nomiDellaVista(await preventivoApi.vista(pid));
      if (n !== richiesta.current || pidRef.current !== pid) return;
      mettiVista(v);
      setAperto((a) => restaAperto(a, v));
      setErrore(false);
    } catch (e) {
      if (n !== richiesta.current || pidRef.current !== pid) return;
      // un preventivo che non c'è (o non è dell'utente): si torna alla lista, senza lasciare questa voce nella cronologia
      if (e instanceof ApiError && e.status === 404 && pid !== "") { navigate("/preventivo-condomini", { replace: true }); return; }
      setErrore(true);
      setRiprova((t) => t + 1);
    }
  }, [mettiVista, pid, navigate]);
  // l'ultima `ricarica`: quella di un invio partito da un altro preventivo non deve toccare la pagina di adesso
  const ricaricaRef = useRef(ricarica);
  ricaricaRef.current = ricarica;

  // Le bollette fotografate: «Bolletta finita» le mette in coda sul telefono e partono una per volta, in sottofondo.
  // una coda per preventivo: `coda:<utente>:<preventivo>` (la vecchia `coda:<utente>` resta com'è, non si migra)
  const chiaveFoto = `coda:${useContext(AuthContext)?.user?.id ?? ""}:${pid}`;
  const foto = useCodaFoto(chiaveFoto, pid, ricarica);

  // Al montaggio la pagina mostra quello che ha il server, nient'altro.
  useEffect(() => { void ricarica(); }, [ricarica]);

  useEffect(() => {
    // Se la prima lettura non è mai riuscita si aspetta «Riprova»; a pagina già aperta si continua da sé.
    if (vista === null && errore) return;
    if (!errore && !(vista && daInterrogare(vista))) return;
    const t = setTimeout(() => { void ricarica(); }, INTERVALLO_MS);
    return () => clearTimeout(t);
  }, [vista, errore, riprova, ricarica]);

  // Un file alla volta: il successivo parte solo quando il precedente ha avuto risposta.
  const svuota = useCallback(async () => {
    if (inVolo.current) return;
    inVolo.current = true;
    while (codaRef.current.length > 0) {
      const lavoro = codaRef.current[0];
      try {
        const leggero = await comprimiFoto(lavoro.file);
        if (lavoro.bollettaId) {
          await preventivoApi.aggiungiPagina(lavoro.pid, lavoro.bollettaId, leggero);
        } else {
          await preventivoApi.carica(lavoro.pid, leggero);
        }
      } catch (e) {
        // Una pagina non aggiunta non è una bolletta: nessuna riga sua (il suo «Rifai la foto» ne creerebbe una nuova,
        // spezzando la bolletta in due); la bolletta resta com'era, con «Aggiungi pagina».
        // La pagina non partita per rete o guasto si può rimandare, accanto alla sua bolletta.
        if (!lavoro.bollettaId) {
          const tetto = troppeBollette(e);
          const rifiutato = tetto || (e instanceof ApiError && (e.status === 413 || e.status === 415));
          setScarti((s) => [...s, { chiave: lavoro.chiave, file: lavoro.file, pid: lavoro.pid, rifiutato, tetto }]);
        } else {
          const id = lavoro.bollettaId;
          setFalliti((f) => ({ ...f, [id]: { tipo: "pagina", files: [...(f[id]?.files ?? []), lavoro.file] } }));
        }
      }
      codaRef.current = codaRef.current.filter((l) => l.chiave !== lavoro.chiave);   // non slice(1): il preventivo può essere stato eliminato e la coda ripulita
      setCoda(codaRef.current);
      await ricaricaRef.current();
    }
    inVolo.current = false;
  }, []);

  const accoda = useCallback((files: File[], bollettaId?: string, dove: string = pidRef.current) => {
    const nuovi = files.map((file) => ({ chiave: `f${++contatore.current}`, file, bollettaId, pid: dove }));
    codaRef.current = [...codaRef.current, ...nuovi];
    setCoda(codaRef.current);
    void svuota();
  }, [svuota]);

  // Il tetto di bollette: si accodano solo i posti liberi, gli altri file sono subito righe non accettate. `liberiRef`
  // si aggiorna a ogni disegno: la scelta del file può arrivare dopo un po'.
  const inserisciBollette = (files: File[], dove: string = pid) => {
    const liberi = Math.max(0, liberiRef.current);
    if (files.length > liberi) {
      const fuoriPosto = files.slice(liberi).map((file) => ({ chiave: `f${++contatore.current}`, file, pid: dove, rifiutato: true, tetto: true }));
      setScarti((s) => [...s, ...fuoriPosto]);
    }
    if (liberi > 0) accoda(files.slice(0, liberi), undefined, dove);
  };
  const aggiungiBollette = () => pick(ACCETTA_FILE, undefined, (files) => inserisciBollette(files, pid));
  // Con `vecchia` la foto nuova prende il posto di una bolletta non letta: prima si elimina quella,
  // e se l'eliminazione fallisce la foto nuova non parte e la riga vecchia resta.
  const eliminaEAccoda = (vecchia: string, files: File[]) => {
    setFalliti((f) => senza(f, vecchia));
    preventivoApi.elimina(pid, vecchia).then(
      () => { accoda(files, undefined, pid); void ricaricaRef.current(); },
      () => setFalliti((f) => ({ ...f, [vecchia]: { tipo: "elimina", files } })),
    );
  };
  // «Rifai la foto» di una bolletta fotografata, sul telefono: riapre il pannello delle pagine; la nuova parte dalla coda
  // e la vecchia si toglie quando è arrivata. Una bolletta di file, o dal computer: la foto sola, come prima.
  const [rifacendo, setRifacendo] = useState<string | null>(null);
  // Cambia il preventivo nell'indirizzo (stessa rotta, componente montato): ciò che era del vecchio si lascia, la vista
  // del nuovo si chiede. La coda dei file e quella delle foto non si toccano: ogni voce sa il suo preventivo.
  const [pidVisto, setPidVisto] = useState(pid);
  if (pidVisto !== pid) {
    setPidVisto(pid);
    setVista(null);
    setErrore(false);
    setScarti([]);
    setAperto(null);
    setFalliti({});
    setSceltaFallita(null);
    setTolti(new Set());
    setRifacendo(null);
    setConfermaElimina(false);
    setEliminaFallita(false);
    setDaEliminare(null);
    setEliminaBollettaFallita(false);
  }
  const rifaiFoto = (vecchia?: string) => {
    const b = vecchia === undefined ? undefined : vista?.bollette_fuori.find((x) => x.id === vecchia);
    if (vecchia !== undefined && b?.nome_file === null && !window.matchMedia?.("(min-width: 1024px)").matches) {
      setRifacendo(vecchia);
      return;
    }
    scegliFoto(vecchia);
  };
  const scegliFoto = (vecchia?: string) => pick(ACCETTA_FOTO, "environment", (files) => {
    if (vecchia === undefined) { accoda(files, undefined, pid); return; }
    eliminaEAccoda(vecchia, files);
  });
  const riprovaScarto = (s: Scarto) => {
    setScarti((x) => x.filter((y) => y.chiave !== s.chiave));
    accoda([s.file], undefined, s.pid);
  };
  const riprovaFallito = (id: string) => {
    const f = falliti[id];
    if (!f) return;
    if (f.tipo === "elimina") { eliminaEAccoda(id, f.files); return; }
    setFalliti((x) => senza(x, id));
    accoda(f.files, id, pid);
  };
  const aggiungiPagina = (id: string) => pick(ACCETTA_FOTO, "environment", (files) => accoda(files.slice(0, 1), id, pid));

  // Una scelta alla volta: finché il server non risponde i menù sono spenti e un secondo cambio non parte.
  // La risposta è la vista nuova; una lettura partita prima non deve riportare la scelta vecchia.
  async function scegli(chiama: () => Promise<Vista>, dove: SceltaFallita["dove"], condominio: string, contatore?: string) {
    if (scegliendoRef.current) return;
    scegliendoRef.current = true;
    setScegliendo(true);
    setSceltaFallita(null);
    const dove_ = pid;   // il preventivo dove la scelta è partita: la risposta non deve finire su un altro
    try {
      const v = nomiDellaVista(await chiama());
      if (pidRef.current !== dove_) return;
      richiesta.current += 1;
      mettiVista(v);
      setAperto((a) => restaAperto(a, v));
      setErrore(false);
    } catch {
      // la vista resta quella di prima: i menù tornano sull'offerta che il server ha
      if (pidRef.current === dove_) setSceltaFallita({ chiama, dove, condominio, contatore });
    } finally {
      scegliendoRef.current = false;
      setScegliendo(false);
    }
  }
  const scegliTutti = (id: string, dove: SceltaFallita["dove"]) => scegli(() => preventivoApi.scegliTutti(pid, id), dove, "");
  const scegliCondominio = (c: Condominio, id: string, dove: SceltaFallita["dove"]) =>
    scegli(() => preventivoApi.scegliCondominio(pid, c.chiave, id), dove, c.chiave);
  const scegliContatore = (c: Condominio, k: Contatore, id: string | null) =>
    scegli(() => preventivoApi.scegliContatore(pid, k.chiave, id), "riga", c.chiave, k.chiave);
  // La vista che torna da un salvataggio dell'amministratore o da un invio: sostituisce quella della pagina (una lettura
  // partita prima non deve riportare indietro i dati appena salvati).
  const vistaSalvata = (v: Vista) => {
    if (pidRef.current !== pid) return;
    richiesta.current += 1;
    mettiVista(nomiDellaVista(v));
  };
  const avvisoScelta = (dove: SceltaFallita["dove"], condominio: string | null, contatore?: string) =>
    sceltaFallita && sceltaFallita.dove === dove && (condominio === null || sceltaFallita.condominio === condominio)
      && (contatore === undefined || sceltaFallita.contatore === contatore)
      ? <AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} onRiprova={() => { void scegli(sceltaFallita.chiama, sceltaFallita.dove, sceltaFallita.condominio, sceltaFallita.contatore); }} />
      : null;
  // i menù di una fornitura, nella tabella e nella scheda: lo stesso avviso, accanto ai menù di quella fornitura
  const avvisoRiga = (k: Contatore) => avvisoScelta("riga", null, k.chiave);
  const avvisoCondominio = (c: Condominio) => avvisoScelta("intestazione", c.chiave);
  const chiediElimina = (b: BollettaVista | BollettaFuori) => {
    setEliminaBollettaFallita(false);
    setDaEliminare({ id: b.id, mese: "mese_label" in b ? b.mese_label : null });
  };

  // La bolletta se ne va col suo file e la sua lettura (il server regge anche una lettura in corso); il link già mandato
  // all'amministratore è una copia ferma e non cambia.
  async function eliminaBolletta() {
    if (daEliminare === null || eliminandoBollettaRef.current) return;
    const { id } = daEliminare;
    const dove_ = pid;
    eliminandoBollettaRef.current = true;
    setEliminandoBolletta(true);
    setEliminaBollettaFallita(false);
    try {
      await preventivoApi.elimina(dove_, id);
      if (pidRef.current !== dove_) return;
      setFalliti((f) => senza(f, id));
      setDaEliminare(null);
      void ricarica();
    } catch {
      // il dialogo resta aperto: si può riprovare o annullare
      if (pidRef.current === dove_) setEliminaBollettaFallita(true);
    } finally {
      eliminandoBollettaRef.current = false;
      setEliminandoBolletta(false);
    }
  }

  async function elimina() {
    if (eliminandoRef.current) return;
    eliminandoRef.current = true;
    setEliminando(true);
    setEliminaFallita(false);
    try {
      await preventivoApi.eliminaPreventivo(pid);
      // le foto e i file ancora in coda di questo preventivo non partono più; le foto lasciano anche il telefono
      foto.scarta(pid, chiaveFoto);
      codaRef.current = codaRef.current.filter((l) => l.pid !== pid);
      setCoda(codaRef.current);
      setScarti((x) => x.filter((y) => y.pid !== pid));
      // `replace`: indietro non deve tornare a un preventivo che non c'è più
      navigate("/preventivo-condomini", { replace: true });
    } catch {
      // il dialogo resta aperto: si può riprovare o annullare
      setEliminaFallita(true);
      eliminandoRef.current = false;
      setEliminando(false);
    }
  }

  // `?condivisi=<id>`: la scelta del preventivo l'ha già fatta la lista (spec § 8), qui i file entrano subito. Si leggono una
  // volta (anche con StrictMode); entrano quando ci sono loro E la vista (serve `massimo_bollette`).
  const avviatoRef = useRef(false);
  useEffect(() => {
    if (condivisiRef.current === "errore") { condivisiRef.current = null; setAllegatiNonRicevuti(true); togliCondivisi(); return; }
    if (!condivisiRef.current || avviatoRef.current) return;
    avviatoRef.current = true;
    const voluta = condivisiRef.current;
    leggiCondivisi().then((r) => {
      // la lista ha scelto per la condivisione `voluta`; in IndexedDB c'è una voce sola e una più nuova la sovrascrive:
      // se non è quella, nulla entra qui e si torna alla lista, che chiede per quella che aspetta davvero
      if (r && r.id !== voluta) { navigate("/preventivo-condomini?condivisi=1", { replace: true }); return; }
      setLetti(r);
    }, () => { setAllegatiNonRicevuti(true); togliCondivisi(); });
  }, [navigate]);
  const decisoRef = useRef(false);
  const vistaPronta = vista !== null;
  useEffect(() => {
    if (letti === undefined || !vistaPronta || decisoRef.current) return;
    decisoRef.current = true;
    if (letti === null) { togliCondivisi(); return; }
    setEntrano(letti);
  }, [letti, vistaPronta]);

  // I file entrano dopo il disegno che ha i posti liberi giusti (anche con una coda già in corso: `liberiRef` conta i
  // posti che la coda e le foto occupano, così il massimo di bollette non si supera); subito dopo si scartano dal telefono.
  useEffect(() => {
    if (entrano === null) return;
    inserisciBollette(entrano.files, pid);
    setEntrano(null);
    scartaCondivisi(entrano.id).catch(() => { /* resta in attesa: la prossima apertura trova un'altra condivisione o questa */ });
    togliCondivisi();
  });

  // le righe «In coda» e i file non accettati sono del preventivo dove i file sono stati aggiunti
  const codaQui = coda.filter((l) => l.pid === pid);
  const scartiQui = scarti.filter((x) => x.pid === pid);
  const totale = vista?.totale ?? 0;
  const fuori = vista?.bollette_fuori ?? [];
  const inLetturaOCoda = fuori.filter((b) => b.stato === "in_lettura" || b.stato === "in_coda");
  const altreFuori = fuori.filter((b) => b.stato !== "in_lettura" && b.stato !== "in_coda");
  // Per la striscia del telefono: le bollette ancora in lavorazione (in invio, in coda, in lettura, o lette con
  // l'analisi di dettaglio in corso) sul totale, con quelle ancora in invio dal telefono; il resto è pronto
  const bolletteTotali = totale + foto.voci.length;
  const inAnalisi = foto.voci.length + inLetturaOCoda.length
    + (vista?.condomini ?? []).flatMap(bolletteDelCondominio).filter((b) => b.dettaglio === "in_corso").length;
  // Posti occupati: le bollette del server, i PDF in coda (non le pagine in più) e le bollette fotografate non ancora
  // accettate (non quelle rifiutate per sempre né quelle che ne sostituiscono una già contata). Senza `massimo_bollette` (vista vecchia) nessun tetto.
  const massimo = vista?.massimo_bollette;
  const occupati = totale + codaQui.filter((l) => !l.bollettaId).length + foto.voci.filter((v) => !v.rifiutato && !v.sostituisce).length;
  const frasePiena = massimo !== undefined && occupati >= massimo ? raggiuntoMassimo(massimo) : undefined;
  liberiRef.current = massimo === undefined ? Infinity : massimo - occupati;
  const conVoci = totale > 0 || codaQui.length > 0 || foto.voci.length > 0 || scartiQui.length > 0;
  const fuoriProps = {
    onPagina: aggiungiPagina,
    onRifai: (id: string) => rifaiFoto(id),
    onElimina: chiediElimina,
    errori: Object.fromEntries(Object.entries(falliti).map(([id, f]) => [id, {
      testo: f.tipo === "pagina" ? INVIO_NON_RIUSCITO : COLLEGAMENTO_NON_RIUSCITO,
      onRiprova: () => riprovaFallito(id),
    }])),
  };

  // «Tutti i condomìni» c'è con almeno un condominio e un'offerta da scegliere. La barra in basso (sotto i 1024 px)
  // vuole il suo spazio in fondo, così l'ultima scheda non resta sotto: sotto i 768 px lo lascia già il layout (pb-24),
  // fra 768 e 1023 lo aggiunge la pagina
  // (un condominio di sole bollette gas non ha un'offerta da scegliere)
  const conTutti = vista !== null && vista.condomini.some((c) => c.contatori.length > 0) && vista.offerte.some((o) => !o.scaduta_il);
  // «Invia» (barra del telefono, riquadro in cima dal computer) con l'invio acceso e qualcosa da mettere nel link
  const conInvio = vista !== null && linkAttivo && condominiDelLink(vista).length > 0;
  const apriInvio = () => setAperto({ dove: "invio" });

  return (
    <ModoPreventivo.Provider value={modo}>
    <StrisciaStato inAnalisi={inAnalisi} pronte={bolletteTotali - inAnalisi} totale={bolletteTotali} />
    <div className={`space-y-5 ${conTutti || conInvio ? "md:pb-20 lg:pb-0" : ""}`}>
      {allegatiNonRicevuti && (
        <div className="rounded-lg border border-border bg-card p-3">
          <AvvisoErrore testo={ALLEGATI_NON_RICEVUTI} />
        </div>
      )}
      <Link
        to="/preventivo-condomini"
        className={`-ml-1 inline-flex h-11 items-center gap-1 rounded-lg px-1 text-sm font-medium text-brand-dark touch-manipulation hover:text-brand ${FOCUS}`}
      >
        <ChevronLeft size={18} aria-hidden="true" />
        {TORNA_LISTA}
      </Link>
      {vista !== null && (
        <RiquadroAmministratore
          key={pid}
          pid={pid}
          amministratore={vista.amministratore ?? { nome: null, email: null, cellulare: null }}
          linkAttivo={linkAttivo}
          onVista={vistaSalvata}
          onPendente={setAdminPendente}
        />
      )}
      <Testata
        totale={totale}
        condomini={vista?.condomini.length ?? 0}
        onAggiungi={aggiungiBollette}
        tetto={frasePiena}
        telefono={
          <FotoBolletta
            onCarica={aggiungiBollette}
            onFinita={(pagine) => foto.aggiungi(pagine, rifacendo ?? undefined)}
            sostituisce={rifacendo}
            haBollette={bolletteTotali > 0}
            tetto={frasePiena}
            onSostituzioneChiusa={() => setRifacendo(null)}
          />
        }
      />

      {vista === null && errore ? (
        <div className="rounded-lg border border-border bg-card p-3">
          <AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} onRiprova={() => { setErrore(false); void ricarica(); }} />
        </div>
      ) : vista === null ? (
        <div aria-busy="true" className="h-24 rounded-lg border border-border bg-card animate-pulse motion-reduce:animate-none" />
      ) : (
        <>
          {!conVoci && (
            <div className="rounded-lg border border-border bg-card p-4 text-sm text-text">
              <p className="font-medium">{APERTURA}</p>
              <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-muted-foreground">
                {APERTURA_PASSI.map((t) => <li key={t}>{t}</li>)}
              </ol>
            </div>
          )}
          {conVoci && (
            <CodaCaricamento lette={vista.lette} totale={totale} inCorso={vista.lette < totale || codaQui.length > 0 || foto.voci.length > 0}>
              {foto.voci.map((v) => (
                <VoceFoto key={v.id} voce={{ ...v, pagine: v.pagine.length }} massimo={massimo} onRiprova={() => foto.riprova(v.id)} onElimina={() => foto.elimina(v.id)} />
              ))}
              <BolletteFuori bollette={inLetturaOCoda} {...fuoriProps} />
              {codaQui.map((l) => <Voce key={l.chiave} icona={<Icona tipo="inCoda" />} nome={l.file.name} stato="In coda" />)}
              {scartiQui.map((s) => (
                <Voce key={s.chiave} icona={<Icona tipo="nonLetta" />} nome={s.file.name}
                  stato={s.tetto && massimo !== undefined ? raggiuntoMassimo(massimo) : s.rifiutato ? FILE_NON_ACCETTATO : INVIO_NON_RIUSCITO} live={!s.rifiutato}>
                  {!s.rifiutato && <PulsanteRiprova onClick={() => riprovaScarto(s)} />}
                </Voce>
              ))}
              <BolletteFuori bollette={altreFuori} {...fuoriProps} />
            </CodaCaricamento>
          )}

          {vista.condomini.some((c) => c.contatori.length > 0) && !vista.offerte.some((o) => !o.scaduta_il) && (
            <p className="text-sm text-muted-foreground">{NESSUNA_OFFERTA}</p>
          )}

          {conTutti && (
            <RiquadroTutti
              className="hidden lg:block"
              vista={vista}
              occupato={scegliendo}
              onScegli={(id) => { void scegliTutti(id, "tutti"); }}
              avviso={avvisoScelta("tutti", null)}
              onInvia={conInvio ? apriInvio : undefined}
            />
          )}
          {!conTutti && conInvio && (
            <TastoInvia onInvia={apriInvio} invii={vista.invii} className="hidden lg:flex lg:flex-col lg:items-end" />
          )}
          {(conTutti || conInvio) && (
            <BarraTutti
              vista={vista}
              onApri={conTutti ? () => setAperto({ dove: "tutti" }) : undefined}
              onInvia={conInvio ? apriInvio : undefined}
            />
          )}

          {/* Sotto 640 px una scheda per condominio; da 640 una tabella sola per tutti, compatta fino a 1023 px e piena da 1024
              (spec 2026-10-23 § 3, Alfonso 25/10). */}
          {vista.condomini.map((c) => (
            <SchedaCondominio
              key={c.chiave}
              className="sm:hidden"
              condominio={c}
              offerte={vista.offerte}
              onCambia={() => setAperto({ dove: "pannello", condominio: c.chiave })}
              onPagina={aggiungiPagina}
              occupato={scegliendo}
              onScegliContatore={(x, k, id) => { void scegliContatore(x, k, id); }}
              onElimina={chiediElimina}
              avvisoRiga={avvisoRiga}
            />
          ))}
          {vista.condomini.length > 0 && (
            <TabellaCondomini
              className="hidden sm:block"
              condomini={vista.condomini}
              offerte={vista.offerte}
              occupato={scegliendo}
              aperto={aperto?.dove === "testata" ? { condominio: aperto.condominio } : null}
              onApriCondominio={(c) => setAperto((a) => (a?.dove === "testata" && a.condominio === c.chiave
                ? null
                : { dove: "testata", condominio: c.chiave }))}
              onChiudi={() => setAperto(null)}
              onPagina={aggiungiPagina}
              onScegli={(c, id) => { void scegliCondominio(c, id, "intestazione"); }}
              onScegliContatore={(c, k, id) => { void scegliContatore(c, k, id); }}
              onElimina={chiediElimina}
              avvisoCondominio={avvisoCondominio}
              avvisoRiga={avvisoRiga}
            />
          )}
        </>
      )}

      {vista !== null && (
        <div className="border-t border-border pt-4">
          <button
            type="button"
            onClick={() => setConfermaElimina(true)}
            className={`inline-flex h-11 items-center gap-2 rounded-lg px-2 text-sm font-medium text-red-700 cursor-pointer touch-manipulation hover:bg-red-50 ${FOCUS}`}
          >
            <Trash2 size={16} aria-hidden="true" />
            {ELIMINA_PREVENTIVO}
          </button>
        </div>
      )}

      <PannelloOfferta
        vista={vista}
        condominio={aperto?.dove === "pannello" ? aperto.condominio : null}
        occupato={scegliendo}
        onScegliCondominio={(c, id) => { void scegliCondominio(c, id, "pannello"); }}
        avviso={avvisoScelta("pannello", null)}
        onChiudi={() => setAperto(null)}
      />

      {vista !== null && linkAttivo && (
        <InvioAmministratore
          aperto={aperto?.dove === "invio"}
          onChiudi={() => setAperto(null)}
          pid={pid}
          vista={vista}
          tolti={tolti}
          onScambia={(k) => setTolti((x) => scambia(x, k))}
          onTutti={(metti) => setTolti(metti ? new Set() : new Set(condominiDelLink(vista).map((c) => c.chiave)))}
          onRifiutato={() => { void ricarica(); }}
          onVista={vistaSalvata}
          bloccato={adminPendente}
        />
      )}

      {vista && (
        <PannelloTutti
          vista={vista}
          aperto={aperto?.dove === "tutti"}
          occupato={scegliendo}
          onScegli={(id) => { void scegliTutti(id, "pannelloTutti"); }}
          avviso={avvisoScelta("pannelloTutti", null)}
          onChiudi={() => setAperto(null)}
        />
      )}

      <ConfirmDialog
        open={confermaElimina}
        title={titoloElimina(vista?.amministratore?.nome?.trim() || null)}
        message={testoElimina(vista?.totale ?? 0)}
        confirmLabel={eliminaFallita ? RIPROVA : ELIMINA}
        error={eliminaFallita ? COLLEGAMENTO_NON_RIUSCITO : null}
        busy={eliminando}
        onConfirm={() => { void elimina(); }}
        onCancel={() => { setConfermaElimina(false); setEliminaFallita(false); }}
      />

      <ConfirmDialog
        open={daEliminare !== null}
        title={titoloEliminaBolletta(daEliminare?.mese ?? null)}
        message={TESTO_ELIMINA_BOLLETTA}
        confirmLabel={eliminaBollettaFallita ? RIPROVA : ELIMINA}
        error={eliminaBollettaFallita ? COLLEGAMENTO_NON_RIUSCITO : null}
        busy={eliminandoBolletta}
        onConfirm={() => { void eliminaBolletta(); }}
        onCancel={() => { setDaEliminare(null); setEliminaBollettaFallita(false); }}
      />

      <ConfirmDialog
        open={uscita.verso !== null}
        title={USCIRE_SENZA_SALVARE}
        message={TESTO_USCITA}
        confirmLabel={ESCI_SENZA_SALVARE}
        onConfirm={() => { const dove = uscita.verso; uscita.chiudi(); if (dove !== null) navigate(dove); }}
        onCancel={uscita.chiudi}
      />
    </div>
    </ModoPreventivo.Provider>
  );
}

/** Il nome con cui la rotta `/preventivo-condomini/:id` importa la pagina (App.tsx). */
export const PreventivoCondominiRotta = PreventivoCondomini;
